/* eslint-disable parog-web/no-glint-expect-error-directive */
import type Store from '@ember-data/store';
import { concat, fn, hash } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { EmberChangeset } from 'ember-changeset';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import t from 'ember-intl/helpers/t';
import and from 'ember-truth-helpers/helpers/and';
import gt from 'ember-truth-helpers/helpers/gt';
import notEq from 'ember-truth-helpers/helpers/not-eq';
import FuiDropdown from 'parog-web/components/fui-dropdown';
import type TherapeuticalArea from 'parog-web/models/therapeutical-area';
import type SessionService from 'parog-web/services/session';
import BasicRadioGroup from 'ui-kit/components/basic-radio-group';

interface Signature {
  Args: {
    changeset: EmberChangeset;
    onTherapeuticalAreaChanged?: (
      therapeuticalArea?: TherapeuticalArea
    ) => void;
  };
}

interface S {
  Args: {
    changeset: EmberChangeset;
    therapeuticalArea: TherapeuticalArea | undefined | null;
    onTherapeuticalAreaChanged?: (
      therapeuticalArea?: TherapeuticalArea
    ) => void;
  };
}

export class FurField extends Component<S> {
  onFollowupReasonChange = (value): void => {
    this.args.changeset.set('followUpReason', value);
    this.args.onTherapeuticalAreaChanged?.(value);
  };

  <template>
    {{#let
      @therapeuticalArea.followUpReasons
      (changesetGet @changeset 'followUpReason')
      as |followUpReasons selected|
    }}
      <div class='field' data-test-update-fur>
        {{! Use a dropdown if we have more than 5 items }}
        {{#if (gt followUpReasons.length 5)}}
          <FuiDropdown
            class='selection'
            @selected={{selected}}
            @settings={{hash onChange=this.onFollowupReasonChange}}
          >
            <:defaultText>Selectionnez un objet de suivi</:defaultText>
            <:menu as |Item|>
              {{#each followUpReasons as |followUpReason|}}
                <Item @value={{followUpReason.id}}>
                  {{t (concat 'follow-up-reason.' followUpReason.id)}}
                </Item>
              {{/each}}
            </:menu>
          </FuiDropdown>
        {{else}}
          <BasicRadioGroup
            @propertyName='followUpReason'
            @current={{selected}}
            {{! @glint-expect-error }}
            @onChange={{this.onFollowupReasonChange}}
            as |Group|
          >
            <div class='fields'>
              {{#each followUpReasons as |followUpReason|}}
                <div class='field'>
                  <Group.Radio
                    @label={{t (concat 'follow-up-reason.' followUpReason.id)}}
                    @value={{followUpReason.id}}
                  />
                </div>
              {{/each}}
            </div>
          </BasicRadioGroup>
        {{/if}}
      </div>
    {{/let}}
  </template>
}

export default class EnrollmentFieldsComponent extends Component<Signature> {
  @service declare session: SessionService;
  @service declare store: Store;

  get therapeuticalArea(): TherapeuticalArea | undefined {
    const followUpReasonId = this.args.changeset.get('followUpReason');

    const followUpReason = this.store.peekRecord(
      'follow-up-reason',
      `${followUpReasonId}`
    );

    return followUpReason?.therapeuticalArea;
  }

  set therapeuticalAreaId(therapeuticalAreaId: string) {
    const therapeuticalArea = this.store.peekRecord(
      'therapeutical-area',
      `${therapeuticalAreaId}`
    );

    this.args.changeset.set(
      'followUpReason',
      therapeuticalArea?.followUpReasons[0]?.id
    );

    this.args.onTherapeuticalAreaChanged?.(therapeuticalArea ?? undefined);
  }

  get therapeuticalAreas(): TherapeuticalArea[] | null {
    const patient =
      this.args.changeset.get('enrollment.patient') ??
      this.args.changeset.get('patient');
    return [patient.therapeuticalArea];
  }

  <template>
    <h4 class='ui header'>Objet du suivi</h4>

    <div class='field'>
      <BasicRadioGroup
        @title='Patient suivi pour'
        @propertyName='therapeuticalArea'
        @isMandatory={{true}}
        @current={{this.therapeuticalArea.id}}
        {{! @glint-expect-error }}
        @onChange={{fn (mut this.therapeuticalAreaId)}}
        as |Group|
      >
        <div class='fields flex-wrap'>
          {{#each this.therapeuticalAreas as |therapeuticalArea|}}
            <div class='field' data-test-field-therapeutical-area>
              <Group.Radio
                @label={{t (concat 'therapeutical-area.' therapeuticalArea.id)}}
                @value={{therapeuticalArea.id}}
              />
            </div>
          {{/each}}
        </div>
      </BasicRadioGroup>
      <span class='ui error text'>
        {{! @glint-expect-error }}
        {{@changeset.error.followUpReason.validation}}
      </span>
    </div>

    {{#if
      (and this.therapeuticalArea (notEq this.therapeuticalArea.id 'generic'))
    }}

      <FurField
        @changeset={{@changeset}}
        @therapeuticalArea={{this.therapeuticalArea}}
        @onTherapeuticalAreaChanged={{@onTherapeuticalAreaChanged}}
      />

    {{/if}}
  </template>
}
